/**
 * `GET /api/dashboard` — docs/05-dev-spec.md §4 contract #11 (task T.C2).
 *
 *   `{ campaign: { totalByStatus, planTotal, lastSnapshotAt }, venues: [DashboardRowDto] }`
 *
 * The generated types for this endpoint exist in `@cb-manager/shared`
 * (`openapi/api-types.ts`) but that module is not re-exported through the barrel
 * yet, so the shapes are declared here 1:1 with the schema instead of reaching
 * into a path that is not part of the package surface.
 *
 * Two shapes unwrap:
 *   - the §4 envelope `{ data: DashboardDto, meta? }` (what T.B3b will send);
 *   - a bare `DashboardDto` (hand-rolled stubs / older builds) — `unwrapEnvelope`
 *     hands a body with no `data` key through untouched.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { AppStatus } from '@cb-manager/shared';

import { buildQuery, requestEnvelope, type Envelope } from './client';

/**
 * One of the six application statuses (docs/05 §4.1). Type-only import on
 * purpose: this module is the api layer, so a page may import it, but a
 * framework-free helper must not (the `unit` Playwright project runs in plain
 * Node, where `import.meta.env` in `./client` does not exist).
 */
export type DashboardStatus = AppStatus;

export type StatusCountMap = Record<DashboardStatus, number>;

/**
 * ADDITIVE (v1.8.0, D1, T.Y9) — the reporting kind of a venue row. `REGISTRY` is
 * every offline venue, `PARTNER_ONLINE` a partner filial whose plan is the
 * partner's SHARED pool. Optional in the type so a payload an older api sent
 * degrades to `REGISTRY` (which every pre-wave-9 row was) instead of a crash.
 */
export type DashboardVenueKind = 'REGISTRY' | 'PARTNER_ONLINE';

export interface DashboardCampaignDto {
  /**
   * The campaign's whole row count (statement 2's `COUNT(*)`) — attributed +
   * unattributed. ADDITIVE (v1.9.95, T.Z248) DECLARATION of an always-sent field: since
   * the display fold the six `totalByStatus` keys stop summing to it, and the
   * «Заявлений» card prints this number instead of the Σ it used to derive. Optional in
   * the type: a payload without it degrades the card to the (now smaller) Σ.
   */
  applications?: number;
  /**
   * SEMANTICS-ONLY (v1.9.95, T.Z248) — the six keys answer the register's `?status=`
   * tokens now (the «Провалил ЕВИ» fact and the «Не прошёл ЕВИ» clock pull the row out
   * of its stored bucket), so a card and its click agree; the folded rows live on
   * `eviFailed` / `eviNotPassed`.
   */
  totalByStatus: StatusCountMap;
  /** Sum of every quota plan in scope. DEPRECATED-описание (v1.8.0): the legacy mixed sum. */
  planTotal: number;
  // ── ADDITIVE (v1.8.0, D1, T.Y9) — the types kept APART ─────────────────────
  /** Σ `planOffline` over the rows — the number the «План» card reads now. */
  planOffline?: number;
  /**
   * Each partner's pool summed ONCE (never 15 × 2 600): the online half of the
   * campaign, shown in its own card and never added to the offline plan.
   */
  planOnline?: number;
  /** The offline-only fill — the metric the «План» card's percentage quotes. */
  fillPercentOffline?: number;
  /**
   * ADDITIVE (v1.9.23, T.Z105) — the contract funnel of the T.Z101 sync, the three
   * fact buckets under the «Прошёл ЕВИ» card (v1.9.24/T.Z111 adds the two enrollment
   * buckets to the same object). Optional like the other additive campaign fields: a
   * payload an older api sent reads as zeros.
   */
  contractFunnel?: ContractFunnelDto;
  /**
   * ADDITIVE (v1.9.25, T.Z113) — the children the worker marked absent from the
   * freshest ЕВИ pass list with no FTP evidence (`evi_failed IS TRUE`), over the
   * same scoped CTE as `totalByStatus`. Optional: an older api reads as 0. The red
   * widget right of «Прошёл ЕВИ» prints it and drills into
   * `/applications?eviFailed=true`.
   */
  eviFailed?: number;
  /**
   * ADDITIVE (v1.9.95, T.Z248) — the rows the «Не прошёл ЕВИ» clock pulled out of
   * «Получил ссылку» (link older than 10 days, no pass, not flagged), the same rows the
   * register's `?status=EVI_NOT_PASSED` filter answers. The red card beside «Провалил
   * ЕВИ» prints it. Optional: an older api reads as 0.
   */
  eviNotPassed?: number;
  /**
   * ADDITIVE (v1.9.52, T.Z176) — the applications the worker's unti2035 sweep marked
   * «Зачислен» (`enrolled IS TRUE`, migration 0043 — the cat.2035.university
   * mirror's own `status` literal folds to `accepted`), over the same scoped CTE as
   * the status buckets. The predicate is the register's `?enrolled=true` one
   * verbatim, so the «Зачислено» card's click answers exactly this row count.
   * DELIBERATELY NOT `contractFunnel.enrolled` (v1.9.24) — that one counts the
   * iSmart `future` platform's own ACCEPTED fact; a different system's word. An
   * older api's payload degrades to 0.
   */
  enrolledCount?: number;
  /**
   * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — «Материалы не открыты»: the applications
   * the iSmart platform has NOT given materials access to, read off OUR
   * `platform_distributions` mirror of their advisory `is_distributed` flag.
   * STRICTLY the mirror's own answer: a row with NO mirror record is NOT counted
   * (their materials fact is UNKNOWN, never «not open»); a NULL `materials_open` is
   * not counted either. A FACT axis beside the funnel, not a funnel status — the
   * display fold is not applied, so this key stays outside the Σ-partition of the
   * status buckets. An older api's payload degrades to 0.
   */
  materialsNotOpen?: number;
  /** Campaign-level freshness — the per-kind max of the latest snapshots. */
  lastSnapshotAt: string | null;
}

/**
 * ADDITIVE (v1.9.23, T.Z105) — the three contract-fact buckets, the same numbers the
 * register's `?contract=signed|not_signed|not_arrived` filter answers for the same
 * scope. A row the sync has never spoken about lands in none of the three.
 */
export interface ContractFunnelDto {
  contractSigned: number;
  contractNotSigned: number;
  contractNotArrived: number;
  /** ADDITIVE (v1.9.24, T.Z111) — `?contract=enrolled` (`enroll_accepted IS TRUE`). */
  enrolled?: number;
  /** ADDITIVE (v1.9.24, T.Z111) — `?contract=declined` (`enroll_declined IS TRUE`). */
  declined?: number;
}

export interface DashboardRowDto {
  venueId: number;
  name: string;
  region: string | null;
  /**
   * The venue's OWN-TYPE plan (the api composes it: `planOffline` for a
   * `REGISTRY` row, the partner's shared pool for a `PARTNER_ONLINE` one). The
   * legacy mixed sum it used to be is gone — the types are never added together.
   */
  plan: number;
  /** ADDITIVE (v1.8.0, D1, T.Y9) — see {@link DashboardVenueKind}; absent reads as `REGISTRY`. */
  kind?: DashboardVenueKind;
  /** ADDITIVE — the venue's offline plan; the number a `REGISTRY` row shows. */
  planOffline?: number;
  /** ADDITIVE — the venue's own online plan (a deliberate 0 on a partner branch). */
  planOnline?: number;
  /**
   * ADDITIVE — the offline fill of the row; the bar a `REGISTRY` row renders.
   * T.Z44: the numerator counts every status except «Отказ» (shared
   * `FILL_COUNTED_STATUSES` base), the plan denominator is unchanged.
   */
  fillPercentOffline?: number;
  /**
   * ADDITIVE (v1.9.37, T.Z144) — the ONLINE fill numerator: the venue's
   * `format = 'ONLINE'` applications except «Отказ» (statement 1's second FILTER
   * column, the T.Z44 base on the second axis). Optional: an older api degrades to 0.
   */
  onlineApplications?: number;
  /**
   * ADDITIVE (v1.9.37, T.Z144) — the ONLINE fill of the row: `onlineApplications /
   * planOnline`, `0` when that plan is 0. The bar the «Онлайн» table renders — its
   * denominator is exactly the plan that table's «План» cell prints (the own online
   * plan on a registry row, the partner's shared pool on a branch). Optional like
   * every additive field.
   */
  fillPercentOnline?: number;
  /**
   * ADDITIVE (v1.9.41, T.Z149) — the partner this branch belongs to (`venues.partner_id`),
   * `null`/absent on a REGISTRY row. The grouping key the partner table pairs with the
   * response's `partnerPools` entries; the per-partner NUMBERS never travel on the row.
   */
  partnerId?: number | null;
  byStatus: StatusCountMap;
  /**
   * ADDITIVE (v1.9.45, T.Z156) — the ONLINE slice of `byStatus`: the same six statuses
   * over `format = 'ONLINE'` applications only. The «Онлайн» quota tables render their
   * «Заявления» cell from this map — the whole-funnel map once made an online-quota row
   * print offline children (the owner's МФЮА case: «1 | 18» beside a 0% online fill).
   * Optional: a payload an older api sent degrades the cell to the whole-funnel map.
   */
  onlineByStatus?: StatusCountMap;
  /**
   * ADDITIVE (v1.9.45, T.Z156) — the OFFLINE slice of `byStatus`, the map the «Офлайн»
   * quota tables render their «Заявления» cell from. An application whose `format` is
   * NULL belongs to NEITHER axis map, so the two need not sum back to `byStatus`.
   */
  offlineByStatus?: StatusCountMap;
  /**
   * DEPRECATED-описание (v1.8.0) — composed over the own-type plan since the api
   * answers it that way; use `fillPercentOffline` for the offline metric.
   * T.Z44 — like every fill on this contract, the numerator excludes «Отказ»;
   * `byStatus` itself stays whole-funnel.
   */
  fillPercent: number;
  /** Latest snapshot in which any application attributed to this venue appeared. */
  lastSnapshotAt: string | null;
}

export interface DashboardDto {
  campaign: DashboardCampaignDto;
  venues: DashboardRowDto[];
  /**
   * ADDITIVE (v1.9.41, T.Z149) — the partners' SHARED online pools, one entry per partner
   * that holds at least one in-scope branch (ordered by `partnerId`). The «Академия Топ
   * (общая квота)» table reads its ONE progress bar from here: `plan` is the partner's
   * pool read once (never Σ over the branches), `filled` the Σ `onlineApplications` over
   * its in-scope branches — the same base the rows' numbers are cut with. Optional: a
   * payload an older api sent degrades the page to the per-branch rows only.
   */
  partnerPools?: PartnerPoolDto[];
}

/**
 * ADDITIVE (v1.9.41, T.Z149) — one partner's SHARED online pool as #11 answers it
 * (1:1 with `apps/api/src/dashboard/dto.ts`). A partner's filials hold NO quotas of
 * their own — `plan` is the ONE denominator for all of them, so the group renders as
 * ONE progress bar instead of per-branch fills.
 */
export interface PartnerPoolDto {
  /** `partners.id` — pairs with the branch rows' `partnerId`. */
  partnerId: number;
  /** `partners.name` — the group table's caption («Академия Топ»). */
  partnerName: string;
  /** The SHARED pool (`partners.plan_online`), read once per partner. */
  plan: number;
  /** Σ `onlineApplications` over the partner's in-scope branches (the T.Z44 base). */
  filled: number;
  /** `filled / plan × 100`, `0` when the pool is 0. */
  fillPercent: number;
  /** The partner's in-scope branches — the row count its table pages over. */
  venues: number;
}

/** The payload plus the optional pagination block (row count == `meta.total`). */
export type DashboardEnvelope = Envelope<DashboardDto>;

/**
 * ADDITIVE (v1.8.9, T.Z56) — the «Отдел» bucket `?department=` takes. Same vocabulary the
 * venues/dashboard «Отдел» selects have used since T.Z42 (`src/departments.ts`); the api
 * resolves it into the venue set whose manager-department matches and narrows the
 * CAMPAIGN BLOCK and the venue rows TOGETHER, so the cards answer the filter the rows do.
 */
export type DashboardDepartment = 'OP' | 'OS' | 'NONE' | 'MIXED';

/**
 * `?region=`, ADDITIVE (v1.8.9, T.Z56) `?department=` and — ADDITIVE (v1.9.37, T.Z144)
 * — `?format=ONLINE|OFFLINE`.
 *
 * `?format=` CORRECTS an earlier note on this file, which said the client "must not
 * send" the old T.F4 filter. The api has taken `?format=` on #11 all along (parsed and
 * bound as `$2::course_format` since v1.2 — it was never an unknown parameter), the Р11
 * decision only took the pills off this page. T.Z144 pairs the parameter with the kind
 * filter: the page sends `format=OFFLINE` under «Офлайн» and `format=ONLINE` under
 * «Онлайн» so every application bucket the cards print is the row count the register
 * answers at the drill-in that carries the same `format=`. «вместе» sends neither.
 *
 * `?kind=REGISTRY|PARTNER_ONLINE` (ADDITIVE v1.9.35, T.Y1) STAYS a documented,
 * accepted parameter of #11 — the server side is untouched — but this page stopped
 * SENDING it (T.Z145, owner 19.09: «quota based approach»): the kind narrowing kept
 * the online-quota REGISTRY venues out of the «Онлайн» response, and a kind-narrowed
 * card count never equalled the `format=`-only register URL its own click answered.
 * The `kind` argument below is kept for the contract-shaped callers; the dashboard
 * page passes `undefined`.
 */
export function dashboardPath(
  region?: string,
  department?: DashboardDepartment,
  kind?: DashboardVenueKind,
  format?: 'OFFLINE' | 'ONLINE',
): string {
  return `/dashboard${buildQuery({ region, department, kind, format })}`;
}

export async function fetchDashboard(
  region: string | undefined,
  signal?: AbortSignal,
  department?: DashboardDepartment,
  kind?: DashboardVenueKind,
  format?: 'OFFLINE' | 'ONLINE',
): Promise<DashboardEnvelope> {
  return requestEnvelope<DashboardDto>('GET', dashboardPath(region, department, kind, format), ['data'], { signal });
}

/** Cache identity: every server-side filter is the key, so a filter change refetches. */
export function dashboardQueryKey(
  region?: string,
  department?: DashboardDepartment,
  kind?: DashboardVenueKind,
  format?: 'OFFLINE' | 'ONLINE',
): readonly [
  string,
  { region: string | null; department: DashboardDepartment | null; kind: DashboardVenueKind | null; format: string | null },
] {
  return ['dashboard', { region: region ?? null, department: department ?? null, kind: kind ?? null, format: format ?? null }];
}

/**
 * One dashboard query per (region, department, kind, format). `useDashboard(undefined)`
 * (the unfiltered campaign, for the region picker) and any filtered call are distinct
 * cache entries, so the page can hold BOTH: the unfiltered response feeds the region
 * picker (a filtered response only knows its own region), the filtered one drives
 * cards + table. With no filter at all the two keys coincide and TanStack serves one
 * request.
 */
export function useDashboard(
  region: string | undefined,
  department?: DashboardDepartment,
  kind?: DashboardVenueKind,
  format?: 'OFFLINE' | 'ONLINE',
) {
  return useQuery({
    queryKey: dashboardQueryKey(region, department, kind, format),
    queryFn: ({ signal }) => fetchDashboard(region, signal, department, kind, format),
    // T.C2: the campaign view refreshes itself; a dashboard that needs a
    // reload to be current is not a dashboard.
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    // A region change swaps the table without collapsing it into a spinner.
    placeholderData: keepPreviousData,
    // Errors surface immediately (the page offers Retry); polling retries on
    // its own schedule anyway.
    retry: 0,
  });
}
